import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { currentSubscription, needsInstall, pushSupported, subscribe, unsubscribe } from "./push.js";

/** Push на этом устройстве: включить, выключить; на iPhone подсказка, как добавить приложение на главный экран. */
export function PushSettingsPage() {
  const { t } = useTranslation();
  const key = useQuery(trpc.cabinet.push.publicKey.queryOptions());
  const [endpoint, setEndpoint] = useState<string | null | undefined>(undefined);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    void currentSubscription().then((s) => setEndpoint(s?.endpoint ?? null));
  }, []);
  const status = useQuery(trpc.cabinet.push.status.queryOptions(endpoint ? { endpoint } : skipToken));
  const save = useMutation(trpc.cabinet.push.subscribe.mutationOptions());
  const remove = useMutation(trpc.cabinet.push.unsubscribe.mutationOptions());
  const refresh = () => queryClient.invalidateQueries(trpc.cabinet.push.status.pathFilter());

  const enable = async () => {
    if (!key.data?.publicKey) return;
    setDenied(false);
    const sub = await subscribe(key.data.publicKey);
    if (!sub) {
      setDenied(true);
      return;
    }
    await save.mutateAsync(sub);
    setEndpoint(sub.endpoint);
    await refresh();
  };
  const disable = async () => {
    const ep = await unsubscribe();
    if (ep) await remove.mutateAsync({ endpoint: ep });
    setEndpoint(null);
    await refresh();
  };

  const on = Boolean(endpoint && status.data?.subscribed);
  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("push.title")}</PageTitle>
      <p className="mb-4 text-sm text-ink-2">{t("push.what")}</p>
      {key.isPending || endpoint === undefined ? (
        <Spinner />
      ) : needsInstall() ? (
        <InstallHint />
      ) : !pushSupported() ? (
        <Notice>{t("push.unsupported")}</Notice>
      ) : !key.data?.publicKey ? (
        <Notice>{t("push.notConfigured")}</Notice>
      ) : (
        <Card className="space-y-3">
          <div className="font-medium">{on ? t("push.on") : t("push.off")}</div>
          {denied && <Notice kind="error">{t("push.denied")}</Notice>}
          {(save.isError || remove.isError) && <Notice kind="error">{t("common.error")}</Notice>}
          {on ? (
            <Button variant="secondary" className="w-full lg:w-auto" disabled={remove.isPending} onClick={() => void disable()}>
              {t("push.disable")}
            </Button>
          ) : (
            <Button className="w-full lg:w-auto" disabled={save.isPending} onClick={() => void enable().catch(() => setDenied(true))}>
              {t("push.enable")}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}

/** Подсказка для iPhone: push придут только из приложения на главном экране. */
export function InstallHint() {
  const { t } = useTranslation();
  return (
    <Card className="space-y-2">
      <div className="font-medium">{t("push.installTitle")}</div>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-2">
        <li>{t("push.installStep1")}</li>
        <li>{t("push.installStep2")}</li>
        <li>{t("push.installStep3")}</li>
      </ol>
    </Card>
  );
}
